import { webPaths } from "@zplanem/web-paths"
import { useEffect, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import { PropertyChat } from "../../components/chat/property-chat"
import { SharedChat } from "../../components/chat/shared-chat"
import { ApiError } from "../../lib/api-client"
import {
	OPEN_CHAT_RAIL_EVENT,
	type OpenChatRailDetail,
	readPropertyChatRailOpen,
	writePropertyChatRailOpen,
} from "../../lib/chat-rail"
import { AIResponseIndicator } from "../components/ai-response-indicator"
import { PageActions } from "../components/page-actions"
import { PageActionsBar } from "../components/page-actions-bar"
import { PageContainer } from "../components/page-container"
import { EditablePropertyDescription } from "../components/property/editable-property-description"
import { EditablePropertyTitle } from "../components/property/editable-property-title"
import { PropertyBottomNav } from "../components/property-bottom-nav"
import { PropertyTopBar } from "../components/property-top-bar"
import { PropertyTypeBadge } from "../components/property-type-badge"
import { ShareModal } from "../components/share-modal"
import { useLastAIResponse } from "../hooks/use-last-ai-response"
import { useLastPropertyId } from "../hooks/use-last-property-id"
import { useProperty } from "../hooks/use-property"
import { usePropertyChannelInvalidators } from "../hooks/use-property-channel-invalidators"
import { usePropertyMemberships } from "../hooks/use-property-memberships"
import { useStickyNonZero } from "../hooks/use-sticky-nonzero"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { useUser } from "../user-context"

export function PropertyDetailPage() {
	const { id } = useParams<{ id: string }>()
	const { data: property, isLoading, error } = useProperty(id)
	const { profile } = useUser()
	// PropertyDTO carries no ownerId; ownership comes from the memberships
	// query (the same source PropertyTopBar uses).
	const { data: memberships } = usePropertyMemberships(property?.id)
	const { data: lastAi } = useLastAIResponse(property?.id)
	const { setLastPropertyId } = useLastPropertyId()
	// Subscribe to property-wide realtime events that need cache
	// invalidation (e.g. membership changes refresh the cached members list).
	usePropertyChannelInvalidators(property?.id)
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [shareOpen, setShareOpen] = useState(false)

	const [chatRailOpen, setChatRailOpen] = useState<boolean>(() =>
		readPropertyChatRailOpen(id),
	)
	useEffect(() => {
		// Reset state from localStorage when navigating between properties so
		// each one remembers its own open/closed preference.
		setChatRailOpen(readPropertyChatRailOpen(property?.id))
	}, [property?.id])
	useEffect(() => {
		if (!property?.id) return
		writePropertyChatRailOpen(property.id, chatRailOpen)
	}, [property?.id, chatRailOpen])
	// Open the rail when a mention toast for THIS property is clicked while
	// already on this page (navigation to the current route is a no-op).
	useEffect(() => {
		if (!property?.id) return
		const handler = (e: Event) => {
			const detail = (e as CustomEvent<OpenChatRailDetail>).detail
			if (detail.projectId === null && detail.propertyId === property.id) {
				setChatRailOpen(true)
			}
		}
		window.addEventListener(OPEN_CHAT_RAIL_EVENT, handler)
		return () => window.removeEventListener(OPEN_CHAT_RAIL_EVENT, handler)
	}, [property?.id])

	const sharedChat = property?.chats?.find((c) => c.kind === "shared")
	const chatUnreadCount = useStickyNonZero(
		sharedChat?.unreadMessageCount ?? 0,
		5000,
	)

	const isNotFound = !id || (error instanceof ApiError && error.status === 404)

	useEffect(() => {
		if (property) setLastPropertyId(property.id)
		else if (isNotFound) setLastPropertyId(null)
	}, [property, isNotFound, setLastPropertyId])

	if (isNotFound) return <Navigate to={webPaths.dashboard()} replace />
	if (isLoading) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-muted-foreground">
				{t.common.loading}
			</div>
		)
	}
	if (error) {
		return (
			<div className="min-h-screen w-full bg-background flex items-center justify-center text-destructive px-6 text-center">
				{error.message}
			</div>
		)
	}
	if (!property) return null

	// Only the owner may edit the name/description in place (ZPL-186).
	const canEdit = !!profile && memberships?.owner.id === profile.id

	return (
		<div className="min-h-screen w-full bg-background flex flex-col">
			<PropertyTopBar
				property={property}
				onShareClick={() => setShareOpen(true)}
				chatRailOpen={chatRailOpen}
				chatUnreadCount={chatUnreadCount}
				chatToggleLabel={t.chat.shared.propertyHeaderName}
				onChatRailToggle={() => setChatRailOpen((x) => !x)}
			/>
			<PageContainer chatRailOpen={chatRailOpen}>
				<PageActionsBar chatRailOpen={chatRailOpen}>
					<PageActions
						property={property}
						onShareClick={() => setShareOpen(true)}
						chatRailOpen={chatRailOpen}
						chatUnreadCount={chatUnreadCount}
						chatToggleLabel={t.chat.shared.propertyHeaderName}
						onChatRailToggle={() => setChatRailOpen((x) => !x)}
					/>
				</PageActionsBar>
				<div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between md:gap-3">
					<EditablePropertyTitle property={property} canEdit={canEdit} />
					{/* Mobile: property type + AI status share one line under the
					    name, left-aligned. Web: a right-aligned vertical stack,
					    top-aligned with the name. The contextual page actions sit
					    in the top strip above the content (mobile keeps them in
					    the fixed bar). */}
					<div className="flex flex-row items-center gap-3 md:flex-col md:items-end md:gap-2 md:shrink-0 md:text-right">
						<PropertyTypeBadge property={property} size="lg" />
						<AIResponseIndicator
							initial={lastAi ?? null}
							propertyId={property.id}
							className="text-sm"
						/>
					</div>
				</div>
				<EditablePropertyDescription property={property} canEdit={canEdit} />

				<PropertyChat propertyId={property.id} hidden={chatRailOpen} />
			</PageContainer>

			<SharedChat
				propertyId={property.id}
				chatId={sharedChat?.id}
				isOpen={chatRailOpen}
				onClose={() => setChatRailOpen(false)}
			/>

			{shareOpen ? (
				<ShareModal
					propertyId={property.id}
					propertyName={property.name}
					onClose={() => setShareOpen(false)}
				/>
			) : null}
			<PropertyBottomNav propertyId={property.id} />
		</div>
	)
}
